import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../store/useAuthStore';
import { formatInr as formatRupees } from '../utils/formatters';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function formatDate(value) {
  if (!value) return 'Not set';
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysRemaining(value) {
  if (!value) return null;
  const diff = new Date(value).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export default function Subscription() {
  const user = useAuthStore((state) => state.user);
  const [status, setStatus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [renewPlan, setRenewPlan] = useState('annual');
  const [renewalQuote, setRenewalQuote] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const isOwnerAdmin = ['doctor_admin', 'owner_admin', 'platform_admin'].includes(user?.role);

  async function load() {
    setLoading(true);
    try {
      const [{ data: statusData }, { data: paymentData }] = await Promise.all([
        api.get('/subscriptions/status'),
        api.get('/subscriptions/payments'),
      ]);
      const { data: pricingData } = await api.get('/subscriptions/public/pricing');
      setStatus(statusData);
      setPayments(Array.isArray(paymentData) ? paymentData : []);
      setPlans(pricingData.plans || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to load subscription.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!isOwnerAdmin) return undefined;
    let active = true;
    setRenewalQuote(null);
    api.post('/subscriptions/renew/quote', { planCode: renewPlan })
      .then(({ data }) => { if (active) setRenewalQuote(data); })
      .catch(() => { if (active) setRenewalQuote({ planCode: renewPlan, message: renewPlan === 'annual' ? 'Renewal pricing will be confirmed securely before checkout.' : 'Your Price Lock applies only to an Annual renewal.' }); });
    return () => { active = false; };
  }, [renewPlan, isOwnerAdmin]);

  const cancel = async () => {
    setError('');
    setMessage('');
    if (!cancelReason.trim()) {
      setError('Cancellation reason is required.');
      return;
    }
    try {
      const { data } = await api.post('/subscriptions/cancel', { reason: cancelReason.trim() });
      setMessage(data.message || 'Subscription cancellation recorded.');
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to cancel subscription.');
    }
  };

  const renew = async () => {
    setError('');
    setMessage('');
    try {
      const { data: order } = await api.post('/subscriptions/renew/create-order', {
        planCode: renewPlan,
      });
      setRenewalQuote(order.quote?.renewalQuote || renewalQuote);
      const ready = await loadRazorpayScript();
      if (!ready || !window.Razorpay) {
        throw new Error('Razorpay Checkout could not be loaded.');
      }
      const razorpay = new window.Razorpay({
        key: order.razorpayKeyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Dektal 360 Pro',
        description: `${order.quote.planName} renewal`,
        order_id: order.orderId,
        handler: async (response) => {
          const { data } = await api.post('/subscriptions/renew/verify-payment', {
            checkoutSession: order.checkoutSession,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          setMessage('Renewal verified successfully.');
          await load();
        },
      });
      razorpay.open();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Renewal could not be started.');
    }
  };

  const subscription = status?.subscription;
  const remaining = daysRemaining(subscription?.currentPeriodEnd || subscription?.trialEndsAt);

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10">
      <div className="mx-auto max-w-5xl">
        <div className="rounded-3xl bg-white p-8 shadow-xl">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500">Clinic Subscription</div>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Subscription Status</h1>
            </div>
            <Link to="/dashboard" className="rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700">
              Back to app
            </Link>
          </div>

          {loading ? <div className="mt-6 text-sm text-slate-500">Loading subscription...</div> : null}
          {error ? <div className="mt-6 rounded-2xl bg-rose-50 p-4 text-sm text-rose-700">{error}</div> : null}
          {message ? <div className="mt-6 rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-700">{message}</div> : null}

          {subscription ? (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="text-xs uppercase text-slate-500">Plan</div>
                <div className="mt-1 font-bold text-slate-900">{subscription.planCode || 'Not set'}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="text-xs uppercase text-slate-500">Status</div>
                <div className="mt-1 font-bold text-slate-900">{subscription.status}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="text-xs uppercase text-slate-500">Valid Until</div>
                <div className="mt-1 font-bold text-slate-900">{formatDate(subscription.currentPeriodEnd || subscription.trialEndsAt)}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="text-xs uppercase text-slate-500">Days Remaining</div>
                <div className="mt-1 font-bold text-slate-900">{remaining === null ? 'Not set' : remaining}</div>
              </div>
            </div>
          ) : (
            !loading && <div className="mt-6 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">No subscription record found. Please renew or contact support.</div>
          )}

          {status?.priceLock ? (
            <div className="mt-6 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
              Price lock active for {status.priceLock.lockedPlanCode}. Renewal price ₹{Number(status.priceLock.lockedRenewalPrice || 0).toLocaleString('en-IN')} until {formatDate(status.priceLock.priceLockEndAt)}.
            </div>
          ) : null}

          <div className="mt-8">
            <h2 className="text-xl font-bold text-slate-900">Payment History</h2>
            <div className="mt-3 space-y-3">
              {payments.length ? payments.map((payment) => (
                <div key={payment.id} className="rounded-2xl border border-slate-200 p-4 text-sm text-slate-700">
                  <div className="font-semibold">{payment.eventType}</div>
                  <div className="mt-1 text-slate-500">{formatDate(payment.createdAt)} · {payment.payload?.payment?.method || 'Razorpay'}</div>
                </div>
              )) : <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">No subscription payments recorded yet.</div>}
            </div>
          </div>

          {isOwnerAdmin ? (
            <div className="mt-8 rounded-2xl border border-slate-200 p-4">
              <h2 className="text-lg font-bold text-slate-900">Renew Subscription</h2>
              {renewalQuote ? <div className={`rounded-2xl border p-4 text-sm ${renewalQuote.priceLockSelected ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-indigo-200 bg-indigo-50 text-indigo-900'}`}>
                {renewalQuote.priceLockSelected ? <span className="inline-flex rounded-full bg-emerald-700 px-3 py-1 text-xs font-bold text-white">Price Lock Applied</span> : null}
                {renewalQuote.officialBasePricePaise != null ? <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5"><span>Current Annual price<br/><b>{formatRupees(Number(renewalQuote.officialBasePricePaise) / 100)}</b></span><span>Selected renewal price<br/><b>{formatRupees(Number(renewalQuote.selectedBasePricePaise) / 100)}</b></span><span>Savings<br/><b>{formatRupees(Number(renewalQuote.savingsPaise) / 100)}</b></span><span>Tax<br/><b>{formatRupees(Number(renewalQuote.taxPaise) / 100)}</b></span><span>Final amount<br/><b>{formatRupees(Number(renewalQuote.totalAmountPaise) / 100)}</b></span></div> : null}
                <p className="mt-3 font-medium">{renewalQuote.priceLockSelected ? 'Your protected Annual renewal price will be used for this payment.' : renewalQuote.message}</p>
                {renewalQuote.selectedProgramme ? <p className="mt-1 text-xs">Programme applied: {renewalQuote.selectedProgramme.replaceAll('_', ' ')}</p> : null}
              </div> : null}
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
                <select value={renewPlan} onChange={(event) => setRenewPlan(event.target.value)} className="rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500">
                  {plans.map((plan) => (
                    <option key={plan.planCode} value={plan.planCode}>
                      {plan.planName} - ₹{Number(plan.sellingPrice || 0).toLocaleString('en-IN')}
                    </option>
                  ))}
                </select>
                <button type="button" onClick={renew} className="rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white">
                  Renew with Razorpay
                </button>
              </div>
            </div>
          ) : null}

          {isOwnerAdmin ? (
            <div className="mt-8 rounded-2xl border border-slate-200 p-4">
              <h2 className="text-lg font-bold text-slate-900">Cancel Auto-Renew</h2>
              <p className="mt-1 text-sm text-slate-600">Access continues until the paid end date. A reason is required for audit safety.</p>
              <textarea value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} className="mt-3 w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500" placeholder="Cancellation reason" />
              <button type="button" onClick={cancel} className="mt-3 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white">
                Cancel auto-renew
              </button>
            </div>
          ) : (
            <div className="mt-8 rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">
              Subscription is managed by clinic owner/admin.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
